<template>
  <cell-base
    :draggable="!hasCompletedJob"
    :class="{
      expanded,
      selected,
      group: true,
      'drag-target': isDragTarget,
      'crossed-and-grayed-out': allJobsComplete,
    }"
    :week-start="weekStart"
    :start="start"
    :end="end"
    :is-saving="isSaving"
    :color="color"
    @click.native.exact.stop="onGroupClick"
    @click.native.meta.stop="onGroupCtrlClick"
    @click.native.ctrl.stop="onGroupCtrlClick"
    @contextmenu.native.prevent.stop="onGroupContextMenu"
    @drag="onGroupDrag"
    @dragover.native="onDragOver"
    @dragenter.native="onDragEnter"
    @dragleave.native="onDragLeave"
    @drop.native="onDrop"
  >
    <template #default>
      <event-cell-job-details
        :job="groupLeader"
        :week-start="weekStart"
        :description="`${jobs.length} jobs | #${groupLeader.id}`"
      >
        <template #prepend>
          <span class="count"
            ><span>{{ jobCountDisplay }}</span></span
          >
        </template>
      </event-cell-job-details>

      <v-icon v-if="allJobsComplete" class="icon" small color="green" data-test="cell-icon"
        >mdi-check-circle-outline</v-icon
      >
      <calendar-grid
        v-if="expanded"
        class="expanded-details"
        :start="expandedCalendarStart"
        :end="expandedCalendarEnd"
      >
        <job-cell
          v-for="job in jobs"
          :key="`group-member__${job.uuid}`"
          :week-start="weekStart"
          :job="job"
          v-on="$listeners"
        />
      </calendar-grid>
    </template>
  </cell-base>
</template>

<script setup lang="ts">
import { computed, inject, Ref, ref, watch } from 'vue'
import moment, { Moment } from 'moment'

import CellBase from '@/components/weekly-schedule/calendar/CellBase.vue'
import JobCell from '@/components/weekly-schedule/JobCell.vue'
import EventCellJobDetails from '@/components/weekly-schedule/EventCellJobDetails.vue'
import CalendarGrid from '@/components/weekly-schedule/CalendarGrid.vue'
import { Item } from '@/components/weekly-schedule/utils'

const props = defineProps<{ jobs: Item[]; weekStart: Moment }>()
const emit = defineEmits<{
  (event: 'drop:group', groupNumber: string, dragEvent: DragEvent)
  (event: 'select:group', assignmentGroupId: string, jobs: Item[], mouseEvent: MouseEvent): void
  (event: 'context:group', assignmentGroupId: string, jobs: Item[], mouseEvent: MouseEvent): void
  (event: 'drag:group', assignmentGroupId: string, jobs: Item[], dragEvent: DragEvent): void
}>()

const isDragging = inject<Ref<boolean>>('isDragging', ref(false))
const expanded = ref(false)
const isDragTarget = ref(false)

const isSaving = computed(() => props.jobs.some((j) => j.isSaving))
const selected = computed(() => props.jobs.every((j) => j.isSelected))

const groupLeader = computed(() => props.jobs[0])

const assignmentGroupId = computed<string>(() => groupLeader.value.assignmentGroupId || '')

const color = computed(() => groupLeader.value.division?.hexCode)
const jobCountDisplay = computed(() => {
  const count = props.jobs.length
  return count > 1 ? `${count}` : ''
})

const start = computed(() => {
  return moment.min(props.jobs.map((j) => j.displayedStartTime))
})
const end = computed(() => {
  return moment.max(props.jobs.map((j) => j.displayedEndTime))
})

const expandedCalendarStart = computed(() => {
  return moment.max(props.weekStart, start.value)
})
const expandedCalendarEnd = computed(() => {
  return moment.min(moment(props.weekStart).endOf('week'), end.value)
})

const allJobsComplete = computed<boolean>(() => {
  return props.jobs.every((j) => j.isCompleted)
})

const hasCompletedJob = computed<boolean>(() => {
  return !!props.jobs.find((job) => job.isCompleted)
})

function onDrop(dragEvent: DragEvent) {
  emit('drop:group', assignmentGroupId.value, dragEvent)
}

function onGroupClick() {
  expanded.value = !expanded.value
}

function onGroupCtrlClick(mouseEvent: MouseEvent) {
  emit('select:group', assignmentGroupId.value, props.jobs, mouseEvent)
}

function onGroupContextMenu(mouseEvent: MouseEvent) {
  emit('context:group', assignmentGroupId.value, props.jobs, mouseEvent)
}

function onGroupDrag(dragEvent: DragEvent) {
  emit('drag:group', assignmentGroupId.value, props.jobs, dragEvent)
}

function onDragOver(dragEvent: DragEvent) {
  dragEvent.preventDefault()
}

function onDragEnter(dragEvent: DragEvent) {
  isDragTarget.value = true

  dragEvent.preventDefault()
}

function onDragLeave() {
  isDragTarget.value = false
}

watch(isDragging, () => {
  if (!isDragging.value) {
    isDragTarget.value = false
  }
})
</script>

<style scoped>
.count {
  background: var(--color);
  color: #fff;
  display: inline-flex;
  align-items: start;
  padding-right: 3px;
  margin-right: 0.3em;
  padding-top: 4px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.group {
  z-index: calc(var(--elevation-base) + 2);
  padding: 0;
}

:deep(.details) {
  pointer-events: none;
}

.expanded-details {
  margin: 2px 0 2px 0;
}

.drag-target .expanded-details {
  pointer-events: none;
}

.drag-target {
  outline: 3px solid var(--color-blue--dark);
}

.crossed-and-grayed-out {
  background-color: var(--color-silver);
}

.cell.crossed-and-grayed-out :deep(.content) {
  color: var(--color-grey);
  text-decoration: line-through;
}

.icon {
  position: absolute;
  top: -3px;
  right: -4px;
  margin: 0.5rem;
}
</style>
